<script setup lang="ts">
    import { computed } from 'vue';
    import List from '@shared/components/List/List.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { type ItemReference, type MoreAction, type OrderByDirection } from '@shared/components/List/ListTypes';
    import GuestListRow from './GuestListRow.vue';
    import {
        ORDER_BY_FIELDS,
        type GuestRow,
        type ParticipantStatusName,
        type EventParticipant,
        type OrderByField,
        type OrderDirection,
    } from './guest-list-types';

    const props = defineProps<{
        rows: GuestRow[];
        selectedIds: string[];
        showCheckboxes: boolean;
        allLoaded: boolean;
        loading: boolean;
        requireAttestation: boolean;
        orderBy: OrderByField;
        orderDirection: OrderDirection;
        selectedAcceptedCount: number;
        acceptedTotal: number;
        exportDisabled: boolean;
        busy: boolean;
        shownCount: number;
        totalCount: number;
        matchingCount: number;
    }>();

    const freeText = defineModel<string>('freeText', { required: true });
    const checkboxesToggled = defineModel<boolean>('checkboxesToggled', { required: true });

    const emit = defineEmits<{
        toggleSelected: [eventParticipantId: string];
        setSelected: [eventParticipantIds: string[]];
        loadMore: [];
        order: [field: OrderByField, direction: OrderDirection];
        addParticipant: [];
        showPublicGuestList: [];
        exportToFile: [];
        exportToCsv: [];
        exportToNewList: [];
        exportToExistingList: [];
        bulkToAttended: [];
        selectedToAttended: [];
        updateStatus: [participant: EventParticipant, status: ParticipantStatusName];
        showResponse: [row: GuestRow];
        showProfile: [contactId: string];
        resendConfirmation: [participant: EventParticipant];
        resendAttestation: [participant: EventParticipant];
    }>();

    const tableHeaders = [
        $Resources.GenericStatus,
        $Resources.EventGuestListParticipantLabel,
        { name: $Resources.EventGuestlistCompanyName },
        $Resources.EventGuestlistCreatedGuest,
        { name: '', classes: 'autofit' },
    ];

    const orderLabels: Record<OrderByField, string> = {
        Status: $Resources.GenericStatus,
        FirstName: $Resources.FirstName,
        LastName: $Resources.LastName,
        Email: $Resources.Email,
        SmsNumber: $Resources.PhoneLabel,
        CompanyName: $Resources.EventGuestlistCompanyName,
        Created: $Resources.EventGuestlistCreatedGuest,
        Modified: $Resources.EventGuestlistModifiedGuest,
    };

    const orderOptions = ORDER_BY_FIELDS.map((field) => ({ key: field, display: orderLabels[field] }));

    const moreActions: MoreAction[] = [{ key: 'showPublicGuestList', display: $Resources.EventPublicGuestListBtnText, icon: 'glyphicon-list' }];

    function onMoreActionClick(key: string) {
        if (key === 'showPublicGuestList') emit('showPublicGuestList');
    }

    const selectedToAttendedText = computed(() => `${$Resources.EventGuestListStatusToAttended} (${props.selectedAcceptedCount})`);

    function rowClass(item: ItemReference) {
        const row = item as GuestRow;
        return {
            'text-line-through': row.participant.StatusName === 'Deleted',
            pointer: props.selectedIds.length === 0,
            parent: !!row.participant.ContactId && !!row.participant.EventParticipantData,
            child: !row.participant.ContactId && row.kind !== 'orphanChild',
        };
    }

    // Clicking a row selects it only while nothing is selected; controls inside the row keep their own behaviour.
    function onRowClick(item: ItemReference, event: MouseEvent) {
        if ((event.target as HTMLElement).closest('a, button, label, input, .dropdown')) return;
        if (props.selectedIds.length === 0) emit('toggleSelected', (item as GuestRow).id);
    }
</script>

<template>
    <List
        displayAs="table"
        itemId="id"
        tableClass="table-event-guestlist"
        serverSideFilter
        hideHeader
        hideDelete
        showCheckAll
        :items="rows"
        :tableHeaders="tableHeaders"
        :rowClass="rowClass"
        :checkedItems="showCheckboxes ? selectedIds : null"
        :allLoaded="allLoaded"
        :loading="loading"
        :filter="freeText"
        :orderOptions="orderOptions"
        :orderByValue="orderBy"
        :orderByDirection="orderDirection"
        :moreActions="moreActions"
        @update:filter="(newFilter: string) => (freeText = newFilter)"
        @update:order="(field: string, direction: OrderByDirection) => emit('order', field as OrderByField, direction)"
        @update:checkedItems="(ids: (string | number)[]) => emit('setSelected', ids as string[])"
        @clearSelected="emit('setSelected', [])"
        @moreActionClick="onMoreActionClick"
        @rowClick="onRowClick"
        @loadMore="emit('loadMore')">
        <template #toolbar>
            <button v-if="selectedIds.length === 0" type="button" class="btn btn-default" @click="emit('addParticipant')">
                <span class="glyphicon glyphicon-user m-xs-r"></span>
                {{ $Resources.AddEventParticipant }}
            </button>
            <BSDropdownButton v-if="selectedIds.length === 0" menuClass="dropdown-menu-right" :disabled="exportDisabled || busy" closeOnClickInside>
                <template #toggle>
                    <span class="glyphicon glyphicon-export m-xxs-r"></span>
                    {{ $Resources.ExportButton }}
                </template>
                <template #content>
                    <li role="presentation" class="dropdown-header">{{ $Resources.ExportToLabel }}:</li>
                    <li>
                        <a href="javascript:void(0)" @click="emit('exportToFile')">{{ $Resources.ExportToFileButton }}</a>
                    </li>
                    <li>
                        <a href="javascript:void(0)" @click="emit('exportToCsv')">{{ $Resources.ExportToCsvButton }}</a>
                    </li>
                    <li>
                        <a href="javascript:void(0)" @click="emit('exportToNewList')">{{ $Resources.ExportToNewListButton }}</a>
                    </li>
                    <li>
                        <a href="javascript:void(0)" @click="emit('exportToExistingList')">{{ $Resources.ExportToExistingListButton }}</a>
                    </li>
                </template>
            </BSDropdownButton>
        </template>

        <template #toolbar-selected>
            <button
                v-if="acceptedTotal > 0"
                type="button"
                class="btn btn-default"
                :class="{ 'is-busy': busy }"
                :disabled="busy"
                @click="emit('bulkToAttended')">
                {{ $Resources.EventGuestListBulkStatusToAttended }} ({{ acceptedTotal }})
            </button>
            <ConfirmButton
                v-if="selectedAcceptedCount > 0"
                :buttonText="selectedToAttendedText"
                buttonClasses="btn-default"
                :disabled="busy"
                undo-button
                @confirm="emit('selectedToAttended')" />
        </template>

        <template #preList>
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-sm m-xxs-t m-sm-b">
                <p class="meta m-0-b">
                    <span class="glyphicon glyphicon-user"></span>
                    {{ $Resources.GenericShowing }} {{ shownCount }} {{ $Resources.GenericOf }} {{ totalCount }}
                    <template v-if="totalCount !== matchingCount">({{ matchingCount }} {{ $Resources.EventGuestListTotalMatching }})</template>
                </p>

                <button type="button" class="btn btn-subtle btn-xs" :disabled="selectedIds.length > 0" @click="checkboxesToggled = !checkboxesToggled">
                    <span class="glyphicon glyphicon-check m-xxs-r" aria-hidden="true"></span>
                    {{ checkboxesToggled ? $Resources.EventGuestListHideCheckboxes : $Resources.EventGuestListShowCheckboxes }}
                </button>
            </div>
        </template>

        <template #item="{ item }: { item: GuestRow }">
            <GuestListRow
                :row="item"
                :requireAttestation="requireAttestation"
                @updateStatus="(status) => emit('updateStatus', item.participant, status)"
                @showResponse="emit('showResponse', item)"
                @showProfile="emit('showProfile', item.participant.ContactId!)"
                @resendConfirmation="emit('resendConfirmation', item.participant)"
                @resendAttestation="emit('resendAttestation', item.participant)" />
        </template>
    </List>
</template>
